import { useMutation } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import type { ConsentRedirect } from './types';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface DecideError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): DecideError {
  const e = new Error(stage) as DecideError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export interface AllowInput {
  client_id: string;
  scopes: string[];
  authorize_request_id: string;
  /** `document_fingerprint` from the consent info the screen showed. */
  document_fingerprint?: string;
}

/**
 * Grants consent and completes the pending authorize flow. Returns the
 * `redirect_uri` (third-party `redirect_uri?code=…&state=…`) for the
 * caller to navigate to.
 */
export function useAllowConsent() {
  const api = useApi();
  return useMutation<ConsentRedirect, DecideError, AllowInput>({
    mutationFn: async (input) => {
      const { data, error, response } = await api.POST('/auth/consents', { body: input });
      if (error || !data || !('redirect_uri' in data) || !data.redirect_uri) {
        throw toError('consent_grant_failed', response, error);
      }
      return data;
    },
  });
}

/**
 * Denies the pending authorize flow. Returns the `redirect_uri` carrying
 * `error=access_denied` for the caller to navigate to.
 */
export function useDenyConsent() {
  const api = useApi();
  return useMutation<ConsentRedirect, DecideError, { authorize_request_id: string }>({
    mutationFn: async (input) => {
      const { data, error, response } = await api.POST('/auth/consents/deny', { body: input });
      if (error || !data?.redirect_uri) {
        throw toError('consent_deny_failed', response, error);
      }
      return data;
    },
  });
}
